import useSWR from 'swr';
import {
    customSWRConfig,
    customSWRPaginatedResponse,
    FieldDefinitionMap,
    IFieldDefinition,
    PaginatedData,
    TicketFormScopeType,
    TicketFormScopeTypeMap
} from '../../model/types';
import { localizedSearchTermForApi } from '../../utils/helpers';
import useSWRCacheKeys from '../useSWRCacheKeys';

type UIFriendlyTicketFieldDefinitionList = {
    list: IFieldDefinition[];
    listMap: FieldDefinitionMap;
};

function getUIFriendlyTicketFieldDefinitionList(
    data: PaginatedData<IFieldDefinition>
): UIFriendlyTicketFieldDefinitionList {
    const list = data?.content;
    const listMap: UIFriendlyTicketFieldDefinitionList['listMap'] = data?.content.reduce(
        (acc, fd) => ({ ...acc, [fd.key]: fd }),
        {}
    );
    return { list, listMap };
}
export default function useTicketFieldDefinitionList<T extends IFieldDefinition>(
    isActiveFields: boolean = null,
    config: customSWRConfig = {},
    scope: TicketFormScopeType = null
): customSWRPaginatedResponse<T> {
    const requestFlag = config?.requestFlag ?? true;

    let url = '';
    switch (isActiveFields) {
        case null:
            url = scope === TicketFormScopeTypeMap.TICKET ? `?scope=${scope}` : '';
            break;
        case true:
            url = `?active${scope === TicketFormScopeTypeMap.TICKET ? `&scope=${scope}` : ''}`;
            break;
        case false:
            url = `?inactive${scope === TicketFormScopeTypeMap.TICKET ? `&scope=${scope}` : ''}`;
            break;
        default:
            url = scope === TicketFormScopeTypeMap.TICKET ? `?scope=${scope}` : '';
            break;
    }
    const keys = useSWRCacheKeys(requestFlag ? `/fields${url}` : null);

    return useSWR<PaginatedData<T>>(keys, { ...config });
}

function usePaginatedTicketFieldDefinitionList<T extends IFieldDefinition>(
    params?: { size?: number; page?: number; isActive?: boolean; sort?: string; searchTerm?: string },
    config: customSWRConfig = {}
): customSWRPaginatedResponse<T> {
    const keys = useSWRCacheKeys(
        `/fields${
            params
                ? `?${params?.isActive ? 'active' : 'inactive'}${params?.size ? `&size=${params?.size}` : ''}${
                      params?.page ? `&page=${params?.page}` : ''
                  }${params?.sort ? `&sort=${params?.sort}` : ''}${
                      params?.searchTerm ? `&searchTerm=${localizedSearchTermForApi(params?.searchTerm)}` : ''
                  }`
                : ''
        }`
    );

    return useSWR<PaginatedData<T>>(keys, { ...config });
}

export {
    getUIFriendlyTicketFieldDefinitionList,
    usePaginatedTicketFieldDefinitionList,
    type UIFriendlyTicketFieldDefinitionList
};
